/**
 * @module userManager
 */
import { Validator } from "jsonschema";
import errors from "../../errors";
import { userErrorCodes } from "./userErrorCodes";
import defaults, { services } from "../../../constants";
import { playbackSchema, mergeSchema } from "../../../schema/index";
import { setCookie } from "../../../utils/cookiesUtils";
import {
  toDevicesCache,
  fromDevicesCache,
  addDeviceToCache,
  addDeviceToMemoryCache,
  toDevicesMemoryCache,
  fromDevicesMemoryCache,
  toUserCache,
  fromUserCache,
  clearUserCache,
  toPreferencesCache,
  fromPreferencesCache,
  toUpdatePreferencesCache,
  toBillingCache,
  fromBillingCache,
  fromPreferencesMemoryCache,
  toPreferencesMemoryCache,
  clearLayoutHeaders,
  toSubscriptionsStatusMemoryCache,
  fromSubscriptionsStatusMemoryCache
} from "./userCacheDecorators";
import { clearMediaCaches } from "../media/mediaCacheDecorators";
import { getErrorTrace } from "../../../errors/traces";
import { isTimeoutError } from "../../../errors/util";
import { cache } from "../../persistence/cache";
import { token } from "../../store";
import { TimeoutError } from "../../../errors/timeoutError";
import deepMerge from "../../../utils/deepMerge";

let isDeviceAdded = false;
const validator = new Validator();

/**
 * Validate the playback user object which contains the subtitles/audio settings for the user
 *
 * @param {Object} playback Receive a formatted body of the playback to validate before sending
 * to the web service in the following format:
 * {
 *   playback: {
 *     "audio": "en",
 *     "subtitles": "off"
 *   }
 * }
 */
function validatePreferencesParams(playback) {
  validator.addSchema(playbackSchema);
  const validation = validator.validate(playback, playbackSchema);

  if (validation.errors.length > 0) {
    throw Error(errors.user.INVALID_USER_PREFERENCES);
  }
}

/**
 * Validate if the token is present and if not to throw a login error
 * @return {boolean}
 */
function validateToken(headers) {
  if (!!headers) {
    return true;
  }
  throw new Error(errors.user.INVALID_USER_INFO);
}

/**
 * Returns the account information for the currently logged in user
 * @returns {Object}  An object representing the user account containing user information like fistName, lastName, email
 * address, parental level, language etc.
 */
async function get() {
  try {
    const response = await this.http.get(
      this.buildUrl("settings", services.user.user),
      await this.getHeaders.call(this)
    );

    if (this.clientType !== "website" && response.data.activeOnAnyAddon) {
      const updatedUserAddOns = await enhanceUserAddOns.call(this, response.data.settings.addons);
      response.data.settings.addons = updatedUserAddOns;
    }
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.user.GENERIC_ERROR} - ${error}`);
  }
}

export async function enhanceUserAddOns(userAddOns) {
  const deepAddOns = await toSubscriptionsStatusMemoryCache(
    fromSubscriptionsStatusMemoryCache(getSubscriptionsStatus)
  ).call(this);

  const updatedUserAddOns = userAddOns.map(userAddOn => {
    let obj = {};
    deepAddOns &&
      deepAddOns.length > 0 &&
      deepAddOns.forEach(deepAddOn => {
        if (userAddOn.name.toLowerCase() === deepAddOn.subscriptionName.toLowerCase()) {
          obj = {
            ...userAddOn,
            // TODO: extend this object on your need with payment Information
            planId: deepAddOn.paymentMethod.paymentMethodParams["planId"] || 0,
            parentPlanId: deepAddOn.paymentMethod.paymentMethodParams["parentPlanId"] || 0
          };
        }
      });
    return obj;
  });

  return updatedUserAddOns;
}

/**
 * Refresh the user data of the local cache
 */
async function refreshCachedUserData() {
  try {
    const userData = await get.call(this, { avoidCache: true });
    const filteredUserData = getUserData(userData);
    setUserCacheData.call(this, filteredUserData);
    return filteredUserData;
  } catch (error) {
    throw Error(`${errors.user.GENERIC_ERROR} - ${error}`);
  }
}

/**
 * Performs a PUT request to the backend with an updated set of properties to be set in the user account. The properties
 * that will be updated in the backend will be those that appear in the user object received as a parameter and will
 * have to match if the object anatomy used in the backend. For example, if the received user object looks like this:
 * const user = {
 *   firstName: "John",
 *   lastName: "Doe",
 *   settings: {
 *     language: "fr",
 *     parental: 18
 *   }
 * }
 * The user account will be updated by changing the values of the properties firstName, lastName, settings.language and
 * settings.parental. The rest of the properties that are already present in the user account will remain unchanged.
 * @param {Object} user An object containing a set of properties with their new values to be stored in the account. The
 * properties structure must match the object's structure stored in the backend.
 */
async function update(user) {
  try {
    const response = await this.http.put(
      this.buildUrl("settings", services.user.user),
      user,
      await this.getHeaders.call(this)
    );
    // Update Parental Control locally (user settings).
    if (response.data.settings.parentalControl !== this.parentalLevel) {
      this.__updatePCL(response.data.settings.parentalControl, this);
    }
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Updates user related data stored in cache.
 * @param {Object} userData - User new data object to be stored.
 * @param {String} newToken - User new token.
 */
async function updateUserCacheData(userData, newToken, needsLayoutCacheReset = false) {
  if (this.localCache && this.localCache.auth) {
    const cachedUserData = (await cache.get("user", { database: "auth" })) || {};
    const data = deepMerge(cachedUserData, userData);
    setUserCacheData.call(this, data);
    newToken && token.updateToken.call(this, newToken);
  }
  await clearUserLayoutCacheData.call(this, needsLayoutCacheReset);
}

/**
 * Sets user related data stored in cache.
 * @param {Object} data - User new data object to be stored.
 */
function setUserCacheData(data) {
  cache.set("user", data, {
    database: "auth",
    expiresIn: this.tokenExpirationTime
  });
}

/**
 * Removes all the locally cached layout's data that belongs to languages other than the current one. For example, if
 * the app has locally cached layouts for a logged user in arabic and french and the user changes the language to
 * english, the layouts' data belonging to arabic and french will be removed. It's important to note that this function
 * only operates on cached layouts for logged in users.
 */
function clearUserLayoutCacheData(needsClearAll) {
  if (this.localCache && this.localCache.layouts) {
    this.supportedLanguages.forEach(language => {
      if (!!needsClearAll || (!needsClearAll && language !== this.lang)) {
        cache.clearAllFilteredKeysFromDatabase("layouts", `${language}-logged`);
      }
    });
  }
}

/**
 * Changes the parental control for the user.
 * @param {number}  level Number for the new parental control level
 */
async function changeParentalLevel(parentalLevel, password) {
  if (await this.authenticationManager.isLoggedIn()) {
    if (!this.parentalControlLevels.includes(parentalLevel)) {
      throw Error(`${errors.user.parentalControl.INVALID_LEVEL} (${parentalLevel})`);
    }
    const levels = this.parentalControlLevels;
    const isUpgradeLevel = levels.indexOf(parentalLevel) > levels.indexOf(this.parentalLevel);
    if (isUpgradeLevel && !password) {
      throw Error(errors.user.parentalControl.PASSWORD_REQUIRED);
    }
    try {
      const data = await update.call(this, {
        settings: {
          parentalControl: parentalLevel
        },
        password,
        usePassword: isUpgradeLevel
      });
      this.__updatePCL(parentalLevel, this);
      const userData = getUserData(data, true);
      const { token } = userData;
      delete userData["token"];
      await updateUserCacheData.call(this, userData, token, true);
      return data;
    } catch (error) {
      console.log("Could not change parental control level in the account");
      this.handleErrorResponse(error);
      throw error;
    }
  }
}

/**
 * Changes the parental control for the user without asking for password.
 * @param {number}  level Number for the new parental control level
 */
async function changeParentalLevelWithoutPwd(parentalLevel) {
  if (await this.authenticationManager.isLoggedIn()) {
    if (!this.parentalControlLevels.includes(parentalLevel)) {
      throw Error(`${errors.user.parentalControl.INVALID_LEVEL} (${parentalLevel})`);
    }


    try {
      const response = await this.http.put(
        this.buildUrl("settings", services.user.users),
        {
          "parental_control": parentalLevel
        },
        await this.getHeaders.call(this)
      );
      // Update Parental Control locally (user settings).
      if (response.data.settings.parentalControl !== this.parentalLevel) {
        this.__updatePCL(response.data.settings.parentalControl, this);
      }
      const data = response.data;
      this.__updatePCL(parentalLevel, this);
      const userData = getUserData(data, true);
      const { token } = userData;
      delete userData["token"];
      await updateUserCacheData.call(this, userData, token, true);
      return data;
    } catch (error) {
      console.log("Could not change parental control level in the account");
      this.handleErrorResponse(error);
      throw error;
    }
  }
}

/**
 * Sets initial parental level after logging in or resets its value to its default.
 */
function setInitialParentalLevel(parentalLevel = defaults.parentalLevel) {
  this.__updatePCL(parentalLevel, this);
}

/**
 * Returns the user data to be set in cache
 * @param {Object} data user data object that contains all information
 * @param {boolean} returnToken indicates if the token should be included in the response object
 */
function getUserData(data, returnToken = false) {
  const {
    globalUserId,
    firstName,
    lastName,
    emailAddress,
    locale,
    settings: { accountStatus, parentalControl, addons, requestedState },
    token
  } = data;
  return {
    globalUserId,
    firstName,
    lastName,
    emailAddress,
    locale,
    settings: { accountStatus, parentalControl, ...(addons && { addons }), requestedState },
    ...(returnToken && { token })
  };
}

/**
 * Sets a new language for the running SDK instance and updates the account language to change the language in every
 * device.
 * @param {String}  language Two character language code that needs to be set ('en' for English, 'fr' for French, 'ar'
 * for Arabic, etc)
 */
async function setLanguage(language) {
  this.lang = language;
  // TODO Consider moving this to a decorator
  if (await this.authenticationManager.isLoggedIn()) {
    try {
      const data = await update.call(this, {
        settings: {
          language
        }
      });
      await updateUserCacheData.call(this, getUserData(data));
    } catch (error) {
      console.log(
        "Could not update language in the account, only changed in the app but the preference is not persistent"
      );
      this.handleErrorResponse(error);
    }
  }
}

/**
 * Gets user devices.
 */
async function getDevices() {
  try {
    const response = await this.http.get(this.buildUrl("settings", services.devices), await this.getHeaders.call(this));
    return response.data.devices;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.user.devices.GENERIC_ERROR} - ${error}`);
  }
}

/**
 * Add a new device to the user devices list.
 * @param {Object} device - Device properties object.
 */
async function addDevice(device) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.devices),
      device,
      await this.getHeaders.call(this)
    );
    isDeviceAdded = true;
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === userErrorCodes.DEVICE_ALREADY_ADDED_STATUS_CODE) {
      return (isDeviceAdded = true);
    }
    let errorToThrow;
    if (isTimeoutError(error)) {
      errorToThrow = new TimeoutError(`${errors.user.devices.ADD_DEVICE_TIMEOUT} - ${error}`);
    } else {
      const errorTrace = getErrorTrace("addDeviceError");
      this.reportsManager.sendReport.call(
        this,
        { env: this.environment, level: "error", event: "Playback" },
        {
          report: errorTrace.code,
          status: "error",
          parameter: {
            errorCode: errorTrace.code,
            dump: { trace: error.message, placeInCode: errorTrace.dump.placeInCode }
          }
        }
      );
      errorToThrow = new Error();
      errorToThrow.message = `${errors.user.devices.DEVICE_NOT_ADDED} - ${error}`;
      errorToThrow.code = error.response && error.response.status;
    }
    throw errorToThrow;
  }
}

function resetDeviceAdded() {
  isDeviceAdded = false;
}

/**
 * Returns if the user has the maximum number of devices reached
 */
async function isMaxDevicesReached(config) {
  try {
    const userDevices = await getDevicesWithMemoryAndCache.call(this, config);
    return userDevices.length === this.maxUserDevices;
  } catch (e) {
    this.handleErrorResponse(e);
    console.log(e);
    return false;
  }
}

/**
 * Returns if the actual device is already in the list of user devices
 */
async function isDeviceRegistered(config) {
  try {
    const userDevices = await getDevicesWithMemoryAndCache.call(this, config);
    const deviceId = await this.device.getId();
    return userDevices.some(device => device.clientIdentifiers.WMDRM === deviceId);
  } catch (e) {
    this.handleErrorResponse(e);
    console.log(e);
  }
}

/**
 * Returns if the actual device have to be included the list of user devices
 */
async function canDeviceBeAdded() {
  try {
    const userDevices = await getDevicesWithMemoryAndCache.call(this);
    const hasDevice = await isDeviceRegistered.call(this);
    return userDevices.length < this.maxUserDevices && !hasDevice && !isDeviceAdded;
  } catch (e) {
    console.log(e);
  }
}

/**
 * Performs several checks in order to know whether a device should be allowed playing content or not. This function
 * does the same as isMaxDevicesReached, isDeviceRegistered and canDeviceBeAdded but combined in a single call. Also,
 * it returns a richer information in one shot because the return value is an object with all the information gathered
 * during the execution of the function.
 * @param {Object} config Configuration object to define how the execution will be done. Check possible properties.
 * @param {Boolean} config.avoidCache If true, this function will always call backend to get devices information. In
 * other case if any of the caches can return the devices list, the cache will be used.
 * @return {Promise<{
 *   deviceAlreadyRegistered: boolean,
 *   freeDeviceSlotsExist: boolean,
 *   deviceId: (string),
 *   allowedToPlay: boolean}>}
 */
async function checkDeviceCanPlay(config) {
  try {
    const userDevices = await getDevicesWithMemoryAndCache.call(this, config);
    const freeDeviceSlotsExist = userDevices.length < this.maxUserDevices;
    const deviceId = await this.device.getId();
    const deviceAlreadyRegistered =
      isDeviceAdded || userDevices.some(device => device.clientIdentifiers.WMDRM === deviceId);
    return {
      allowedToPlay: deviceAlreadyRegistered || freeDeviceSlotsExist || isDeviceAdded,
      freeDeviceSlotsExist,
      deviceAlreadyRegistered,
      deviceId
    };
  } catch (e) {
    this.handleErrorResponse(e);
    console.log(e);
    return e;
  }
}

/**
 * Do a delete request to remove a user device
 *
 * @param {string} deviceId Id of the device to delete
 * @return {Promise}
 */
async function deleteDevice(deviceId) {
  try {
    const url = `${this.buildUrl("settings", services.devices)}?deviceId=${deviceId}`;
    return await this.http.delete(url, await this.getHeaders.call(this));
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Performs a GET request to the user preferences services to get the latest data set to the
 * current user to keep it synced along all devices
 *
 * @returns {Object} Playback user preferences object
 */
async function getPreferences() {
  try {
    const headers = await this.getHeaders.call(this);
    validateToken(headers);

    const preferences = await this.http.get(this.buildUrl("settings", services.user.preferences), headers);
    return preferences.data;
  } catch (e) {
    this.handleErrorResponse(e);
    throw Error(errors.user.INVALID_USER_INFO);
  }
}

/**
 * Performs a POST request to the user preferences services and returning the updated playback object which
 * stored in the WS
 *
 * @param {Object} playback get the params to update user playback prefernces in the following format
 * Example of params object:
 * {
 *   audio: "en",
 *   subtitles: "ar"
 * }
 *
 * @returns {Object} Playback user preferences object
 */
async function updatePreferences(playback = {}) {
  const playbackBody = { playback };
  const headers = await this.getHeaders.call(this);
  validateToken(headers);
  validatePreferencesParams(playbackBody);

  const preferences = await this.http.post(this.buildUrl("settings", services.user.preferences), playbackBody, headers);
  return preferences.data;
}

async function getBilling() {
  try {
    const response = await this.http.get(
      this.buildUrl("settings", services.user.billing),
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    throw Error(`${errors.user.devices.GENERIC_ERROR} - ${error}`);
  }
}

const getDevicesWithMemoryAndCache = toDevicesCache(
  toDevicesMemoryCache(fromDevicesMemoryCache(fromDevicesCache(getDevices)))
);

/**
 * Validates the object which will be sent in /merge request.
 * @param {Object} mergeInfo - Merge data object.
 * @private
 */
function mergeValidation(mergeInfo) {
  const validation = validator.validate(mergeInfo, mergeSchema);
  if (validation.errors.length) {
    throw Error(`${errors.user.mergeAccount.INVALID_MERGE_ACCOUNT_PARAMS} - ${validation.errors.join(" ")}`);
  }
}

/**
 * Merges other accounts (Facebook, etc.) with existing STARZ PLAY account
 * @param {Object} config Merge data object.
 */
async function mergeAccounts(config = {}) {
  // FIXME This function should not accept the social token. It should be kept in internal memory of the SDK instead
  mergeValidation(config);
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.merge),
      config,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Performs a GET request to the service to get the status of the user's addons subscriptions
 *
 * @returns {Object} User's addons subscriptions status object
 */
async function getSubscriptionsStatus(data) {
  try {
    const queries = data && data.addonName ? `addonName=${data.addonName}` : null;
    const response = await this.http.get(
      `${this.buildUrl("settings", services.user.subscriptionsStatus)}${!!queries ? `?${queries}` : ""}`,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Triggers a request to activate the addon subscription
 * @param {Object} data
 * @param {String} data.addonName - Mandatory. Name of the addon to subscribe.
 * @param {Object} data.addonSubscriptionData - Mandatory. Data object that contains the subscription information related.
 * @param {String} data.addonSubscriptionData.paymentPlanId - Mandatory. Id of the payment plan.
 * @param {String} data.addonSubscriptionData.paymentMethodName - Optional. Name of the payment method.
 * @param {Object} data.addonSubscriptionData.paymentMethodParams - Optional. Data object that contains the payment method information related
 * @param {String} data.addonSubscriptionData.paymentMethodParams.language - Optional. Language
 * @param {String} data.addonSubscriptionData.paymentMethodParams.pin - Optional. Pin of the card used in the payment
 * @param {String} data.addonSubscriptionData.paymentMethodParams.token - Optional. Token used in the payment
 * @param {String} data.addonSubscriptionData.paymentMethodParams.customerBillingEmail - Optional. User's emailcustomerBillingEmail
 * @public
 */
async function activateAddonSubscription(data = {}) {
  try {
    const timeout = this.networkRequestTimeouts.activateChannel;
    const response = await this.http.post(this.buildUrl("settings", services.user.addonActivation), data, {
      ...(timeout !== undefined && { timeout }),
      ...(await this.getHeaders.call(this))
    });
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Deactivate the user subscription
 * @param {Object} requestBody
 * @param {String} requestBody.deactivationReason Reason for deactivation
 * @param {String} requestBody.requestedState State of deactivation
 * @param {String} requestBody.deactivationText Additional text mentioning reason of deactivation
 * @public
 */
async function deactivateSubscription(requestBody) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.subscriptionDeactivation),
      requestBody,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Triggers a request to deactivate the addon subscription
 * @param {Object} data
 * @param {String} data.addonName - Mandatory. Name of the addon to unsubscribe.
 * @param {Object} data.addonsDeactivationData - Mandatory. Data object that contains the desubscription information related.
 * @param {String} data.addonsDeactivationData.deactivationReason - Optional. reason of channel deactivation.
 * @param {String} data.addonsDeactivationData.deactivationText - Mandatory. Text of deactivation.
 * @public
 */

async function deactivateAddonSubscription(data = {}) {
  try {
    const timeout = this.networkRequestTimeouts.activateChannel;
    const response = await this.http.post(this.buildUrl("settings", services.user.addonDeactivation), data, {
      ...(timeout !== undefined && { timeout }),
      ...(await this.getHeaders.call(this))
    });
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Generate pin and if not to throw error
 * @return Array<Object> A list of objects representing the validation details
 */
async function generatePin() {
  try {
    const response = await this.http.get(
      this.buildUrl("settings", services.user.generatePin),
      await this.getHeaders.call(this)
    );

    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * login using pin and if not to throw error
 * @return Array<Object> A list of objects representing the validation details
 */
async function loginPin(data) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.loginPin),
      data,
      await this.getHeaders.call(this)
    );

    if (response.data.webToken !== "false") {
      // to clear Index DB layouts cache of logged out user
      cache.clear({ database: "layouts" });

      const jwtCookie = this.jwtCookie;
      jwtCookie ? setCookie(jwtCookie.name, response.data.webToken, jwtCookie.expirationTime) : null;
      token.updateToken.call(this, response.data.webToken);
    }

    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Get concurrent streaming devices and if not to throw error
 * @return Array<Object> A list of objects containg concurrent streaming details
 */
async function getConcurrency() {
  try {
    const response = await this.http.get(
      this.buildUrl("settings", services.user.concurrency),
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

function updateUserToken(userToken) {
  try {
    token.updateToken.call(this, userToken);
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

function updateUserCookie(token) {
  try {
    const jwtCookie = this.jwtCookie;
    jwtCookie ? setCookie(jwtCookie.name, token, jwtCookie.expirationTime) : null;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

export default {
  get: toUserCache(fromUserCache(get)),
  update,
  setLanguage: clearLayoutHeaders(clearMediaCaches(setLanguage)),
  changeParentalLevel: clearLayoutHeaders(clearMediaCaches(changeParentalLevel)),
  changeParentalLevelWithoutPwd: clearLayoutHeaders(clearMediaCaches(changeParentalLevelWithoutPwd)),
  setInitialParentalLevel,
  /**
   * Gets the devices attached to the current user account. The source from where the data is taken is from local cache
   * if there is any devices data available on it, and from th backend in case there is no locally stored information.
   * Also, it's possible to bypass the local cache and force a network call by passing a config object with a property
   * called avoidCache = true.
   * @returns Array<Object> A list of objects representing the devices attached to the account
   */
  getDevices: getDevicesWithMemoryAndCache,
  addDevice: addDeviceToCache(addDeviceToMemoryCache(addDevice)),
  resetDeviceAdded,
  isMaxDevicesReached,
  canDeviceBeAdded,
  isDeviceRegistered,
  checkDeviceCanPlay,
  deleteDevice,
  getPreferences: toPreferencesCache(
    toPreferencesMemoryCache(fromPreferencesMemoryCache(fromPreferencesCache(getPreferences)))
  ),
  updatePreferences: toPreferencesMemoryCache(toUpdatePreferencesCache(updatePreferences)),
  getBilling: getBilling,
  userErrorCodes,
  mergeAccounts,
  getSubscriptionsStatus: toSubscriptionsStatusMemoryCache(fromSubscriptionsStatusMemoryCache(getSubscriptionsStatus)),
  activateAddonSubscription,
  deactivateAddonSubscription,
  refreshCachedUserData,
  validatePin,
  loginPin,
  generatePin,
  deactivateSubscription,
  updateUserToken,
  updateUserCookie,
  checkEvoucherEligiblity,
  checkEvoucherValidation,
  checkEvoucherDeviceValidity,
  eVoucherProvision,
  getConcurrency
};

/**
 * Validate if the pin is present and if not to throw error
 * @return Array<Object> A list of objects representing the validation details
 */
async function validatePin(data) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.validatePin),
      data,
      await this.getHeaders.call(this)
    );

    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

const DEVICE_ID = "AAQQQQ";
/**
 * Creates request body object to be sent in eVoucher eligibility request.
 */
async function createEvoucherEligibilityRequestBody(baseVoucherCode, addonVoucherCode) {
  return {
    deviceId: DEVICE_ID,
    modelId: (await this.systemManager.device.getModelCode()) || "",
    baseVoucherCode,
    addonVoucherCode
  };
}

/**
 * Checks user's device voucher eligibility.
 */
export async function checkEvoucherEligiblity(country, duration, platform = "samsung") {
  try {
    await checkEvoucherValidation.call(this);
  } catch (error) {
    throw error;
  }
  try {
    const model_id = DEVICE_ID;
    const response = await this.http.get(
      `${this.backendHosts.settings}${services.eVoucherEligibility}?model=${model_id}&country=${country}&platform=${platform}&duration=${duration}`
    );
    if (response.data.status !== "VALID") {
      throw new Error("Eligibility status is invalid");
    }
    return response.data;
  } catch (error) {
    error.customCode = "EvoucherEligibiltyFailed";
    error.customMessage = "Couldn't retrieve voucher eligiblity information";
    throw error;
  }
}

/**
 * Checks user's device validity.
 */
export async function checkEvoucherDeviceValidity(platform = "samsung") {
  try {
    const response = await this.http.get(
      `${this.backendHosts.settings}${services.eVoucherDeviceValidation}?platform=${platform}`
    );

    const deviceYear = (await this.device.getYear()) || 2020;

    return deviceYear >= response.data.validFrom && deviceYear <= response.data.validTo ? true : false;
  } catch (error) {
    throw new Error(`Couldn't retrieve voucher device validity information -> ${error}`);
  }
}

/**
 * Checks user's device voucher validation.
 */
export async function checkEvoucherValidation() {
  try {
    const model_id = DEVICE_ID;
    const response = await this.http.get(
      `${this.backendHosts.settings}${services.eVoucherValidation}?deviceId=${model_id}`
    );

    if (response.data.status !== "VALID") {
      throw new Error("Validation status is invalid");
    }
    return response.data;
  } catch (error) {
    error.customCode = "EvoucherValidationFailed";
    error.customMessage = "Couldn't retrieve voucher validtion information";
    throw error;
  }
}

/**
 * Checks user's device voucher eligibility.
 */
export async function eVoucherProvision(baseVoucherCode, addonVoucherCode) {
  try {
    const timeout = this.networkRequestTimeouts.eVoucherProvision;
    const response = await this.http.post(
      `${this.backendHosts.settings}${services.eVoucherProvision}`,
      await createEvoucherEligibilityRequestBody.call(this, baseVoucherCode, addonVoucherCode),
      {
        ...(timeout !== undefined && { timeout }),
        ...(await this.getHeaders.call(this))
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(`Couldn't provision the voucher -> ${error}`);
  }
}
